import React from "react";
import { observer } from "mobx-react";
import moment from "moment";
import { Alert, Box, Typography, useTheme } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { useStore } from "utils/useStore";
import AgentStore from "../Stores/AgentStore";
import KboUserAgreementStore from "../Stores/KboUserAgreementStore";
import type { KboUserAgreementDocument } from "../Models/KboUserAgreement";
import { KboUserAgreementTerms } from "./KboUserAgreementTerms";

type Props = {
    agreement: KboUserAgreementDocument;
};

export const KboUserAgreementCard = observer(({ agreement }: Props) => {
    const agentStore = useStore(AgentStore);
    const store = useStore(KboUserAgreementStore);
    const theme = useTheme();

    const isSaving = store.saveState === "saving";
    const saveFailed = store.saveState === "failed";
    const canAgree = store.canAccept && store.hasReadAgreement;

    // Effective dates are whole days in UTC; local time would show the day before in the US.
    const effectiveOn = moment.utc(agreement.effectiveOn).format("MMMM D, YYYY");

    let hint: string;
    if (!store.canAccept) {
        hint = "You're viewing KBo as another user. Only they can accept.";
    } else if (saveFailed) {
        hint = "Your reading progress is kept. Try saving again.";
    } else if (store.hasReadAgreement) {
        hint = "Agree to start using KBo.";
    } else {
        hint = "Scroll to the end of the agreement to enable Agree.";
    }

    return (
        <Box
            id="kbo-user-agreement-card"
            sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: 1.5,
                padding: 2.5,
                border: `1px solid ${theme.palette.gray[200]}`,
                borderRadius: `${theme.shape.borderRadius * 2}px`,
                backgroundColor: theme.palette.white.main,
            }}
        >
            <Box>
                <Typography
                    sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        lineHeight: "16px",
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: theme.palette.primary.main,
                    }}
                >
                    Welcome to KBo
                </Typography>
                <Typography
                    component="h2"
                    sx={{
                        marginTop: 0.5,
                        fontSize: "18px",
                        fontWeight: 600,
                        lineHeight: "28px",
                        color: theme.palette.gray[900],
                    }}
                >
                    Before you start with KBo
                </Typography>
                <Typography
                    sx={{
                        marginTop: 0.5,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: theme.palette.gray[600],
                    }}
                >
                    {agreement.organizationName} asks everyone with partner
                    access to review and accept these terms before using KBo.
                    It takes a couple of minutes, and you'll only need to do it
                    once.
                </Typography>
                <Typography
                    sx={{
                        marginTop: 1,
                        fontSize: "12px",
                        lineHeight: "16px",
                        color: theme.palette.gray[500],
                    }}
                >
                    {agreement.title} · Version {agreement.version} · Effective{" "}
                    {effectiveOn}
                </Typography>
            </Box>
            <KboUserAgreementTerms agreement={agreement} />
            {saveFailed && (
                <Alert severity="error" id="kbo-user-agreement-save-failed">
                    We couldn't save your agreement, so KBo is still locked.
                    Check your connection and try again. If it keeps happening,
                    contact support@authenticx.com.
                </Alert>
            )}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                }}
            >
                <Typography
                    id="kbo-user-agreement-hint"
                    sx={{
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: canAgree
                            ? theme.palette.secondary[800]
                            : theme.palette.gray[600],
                    }}
                >
                    {hint}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <AcxButton
                        id="kbo-user-agreement-not-now"
                        variant="text"
                        fullWidth={false}
                        disabled={isSaving}
                        onClick={() => store.collapse()}
                    >
                        Not now
                    </AcxButton>
                    <AcxButton
                        id="kbo-user-agreement-agree"
                        fullWidth={false}
                        disabled={!canAgree}
                        disabledTooltip={
                            store.canAccept
                                ? "Scroll to the end of the agreement to enable"
                                : "Only the user can accept the agreement"
                        }
                        loading={isSaving}
                        onClick={() => void agentStore.acceptUserAgreement()}
                    >
                        {saveFailed ? "Try again" : "Agree and continue"}
                    </AcxButton>
                </Box>
            </Box>
        </Box>
    );
});
